import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  ListItemText,
  Checkbox,
  OutlinedInput,
  TextField,
  InputAdornment,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import BlackWhiteSwitch from '../components/common/BlackWhiteSwitch';
import CreateButtonWithModal from '../components/common/CreateButtonWithModal';
import SearchIcon from '@mui/icons-material/Search';

// Mock data creation
function createData(
  servicesFeesName: string,
  countryMarket: string,
  customerGroup: string,
  supplier: string,
  officeID: string,
  journeyType: string,
  cabinClass: string,
  firstDetails: Record<string, string>
) {
  return {
    servicesFeesName,
    countryMarket,
    customerGroup,
    supplier,
    officeID,
    journeyType,
    cabinClass,
    firstDetails
  };
}

// Sample rows
const rows = [
  createData(
    'Fee A',
    'India',
    'Retail',
    'Supplier X',
    'OFF101',
    'Round-Trip',
    'Economy',
    {
      'Brand Fares': 'Brand A',
    'Flight Type': 'Domestic',
    'From Date': '2023-01-01',
    'To Date': '2023-12-31',
    'Fare Type': 'Discounted',
    'Airlines Name': 'Airline A',
    'To Country': 'USA',
    'From Any Airport': 'Yes',
    'To Any Airport': 'No',
    'Currency': 'INR',
    'Equipement Type': 'Airbus A320',
    'Markup by Passenger Type': 'Percentage',
    'Services Fees Only': 'Yes',
    'Markup Type': 'Percentage',
    'Value USD $': '15',
    'Tiered Markup (Price Band)': 'No',
    'Payment Method Surcharge': 'No',
    'Status': 'Active',
    }
  ),
];

// Subtable fields
const fareFields = ['Brand Fares', 'Flight Type', 'From Date', 'To Date', 'Fare Type', 'Airlines Name', 'To Country', 'From Any Airport', 'To Any Airport'];
const airlineFields = ['Currency', 'Equipment Type', 'Markup by Passenger Type', 'Services Fees Only', 'Markup Type', 'Value USD $', 'Tiered Markup (Price Band)', 'Payment Method Surcharge', 'Status'];

// Subtable component
function HorizontalSubtable({
  title,
  fields,
  firstDetails,
}: {
  title: string;
  fields: string[];
  firstDetails: Record<string, string>;
}) {
  return (
    <>
    
    <Box mb={4} className="innerTable">
      <Table size="small">
        <TableHead>
          <TableRow>
            {fields.map((field) => (
              <TableCell key={field} sx={{ fontWeight: 'bold' }}>{field}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {fields.map((field) => (
              <TableCell key={field}>{firstDetails[field] || '-'}</TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
    </>
  );
}
 
// Expandable row
function Row({ row }: { row: ReturnType<typeof createData> }) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
    
      <TableRow>
        <TableCell>{row.servicesFeesName}</TableCell>
        <TableCell>{row.countryMarket}</TableCell>
        <TableCell>{row.customerGroup}</TableCell>
        <TableCell>{row.supplier}</TableCell>
        <TableCell>{row.officeID}</TableCell>
        <TableCell>{row.journeyType}</TableCell>
        <TableCell>{row.cabinClass}</TableCell>
        <TableCell align="center">
          <IconButton size="small" onClick={() => setOpen(!open)} className="viewBtn">
            {open ? (
              <>
                View Less <KeyboardArrowUpIcon />
              </>
            ) : (
              <>
                View More <KeyboardArrowDownIcon />
              </>
            )}
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0 }} className='innerTableBG'>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={2}>
              <HorizontalSubtable
                title=""
                fields={fareFields}
                firstDetails={row.firstDetails}
              />
              <HorizontalSubtable
                title=""
                fields={airlineFields}
                firstDetails={row.firstDetails}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

const names = [
  'Oliver Hansen',
  'Van Henry',
  'April Tucker',
  'Ralph Hubbard',
  'Omar Alexander',
  'Carlos Abbott',
  'Miriam Wagner',
  'Bradley Wilkerson',
  'Virginia Andrews',
  'Kelly Snyder',
];

// Main Component
export default function FlightServiceFees() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('');
  const handleSearch = () => {
      console.log(`Searching for "${searchTerm}" by ${filterBy}`);
    };
  
    const [personName, setPersonName] = React.useState<string[]>([]);
  
    const handleChange = (event: SelectChangeEvent<typeof personName>) => {
      const {
        target: { value },
      } = event;
      setPersonName(
        // On autofill we get a stringified value.
        typeof value === 'string' ? value.split(',') : value,
      );
    };

    
  return (
    <>
    <Box className="headingBtnWrap">
    <Typography variant="h1">Service Fees</Typography>
            <CreateButtonWithModal modalTitle="Service Fees">
              <Box className="modalFormWrap">
            <Box component="form" className="modalForm">
              <Box className="feeModalForm">
                <Typography variant="subtitle1" gutterBottom>User Targeting</Typography>
                <Box className="feeInputFieldWrap">
                {['Services Fees Name' ].map((label) => (
                  <div className="form-control inputFieldWrap" key={label}>
                    <label>{label}</label>
                    <input type="text" className="inputField" />
                  </div>
                ))}
                </Box>
                <Typography variant="subtitle1" gutterBottom>Source</Typography>
                <Box className="feeInputFieldWrap">
                {['Country / Market', 'Customer Group', 'Supplier', 'Office ID' ].map((label) => (
                  <div className="form-control inputFieldWrap" key={label}>
                    <label>{label}</label>
                    <input type="text" className="inputField" />
                  </div>
                ))}
                </Box>
                <Typography variant="subtitle1" gutterBottom>Trip Criteria</Typography>
                <Box className="feeInputFieldWrap">
                {['Journey Type', 'Cabin Class', 'Brand Fares', 'Flight Type', 'From Date', 'To Date' ].map((label) => (
                  <div className="form-control inputFieldWrap" key={label}>
                    <label>{label}</label>
                    <input type="text" className="inputField" />
                  </div>
                ))}
                </Box>
                <Typography variant="subtitle1" gutterBottom>Route Criteria</Typography>
                <Box className="feeInputFieldWrap">
                {['Fare Type', 'Airlines Name', 'From Country', 'To Country', 'From Any Airport', 'To Any Airport' ].map((label) => (
                  <div className="form-control inputFieldWrap" key={label}>
                    <label>{label}</label>
                    <input type="text" className="inputField" />
                  </div>
                ))}
                </Box>
                <Typography variant="subtitle1" gutterBottom>Pricing Logic</Typography>
                <Box className="feeInputFieldWrap">
                {['Currency', 'Device Type', 'Markup by Passenger Type', 'Services Fees On', 'Value USD $', 'Tiered Markup (Price Band)', 'Payment Method Surcharge' ].map((label) => (
                  <div className="form-control inputFieldWrap" key={label}>
                    <label>{label}</label>
                    <input type="text" className="inputField" />
                  </div>
                ))}
                </Box>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-end">
                <Button variant="contained" className='formBtn'>Save</Button>
                </Box>
                </Box>
                <BlackWhiteSwitch />
          </CreateButtonWithModal>
          </Box>
          <Box
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}
        className="searchFilterDropdownWrap"
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search Filter"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearch} edge="start">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 120 }}>
           <Select
      labelId="demo-multiple-checkbox-label"
      id="demo-multiple-checkbox"
      multiple
      value={personName}
      onChange={handleChange}
      displayEmpty
      input={<OutlinedInput />}
      renderValue={(selected) => {
        if (selected.length === 0) {
          return <em>Search Filter</em>;  // <-- Placeholder text
        }
        return selected.join(', ');
      }}
      MenuProps={MenuProps}
    >
      <MenuItem value="">
        <em>Search Filter</em>
      </MenuItem>
      {names.map((name) => (
        <MenuItem key={name} value={name}>
          <Checkbox checked={personName.includes(name)} />
          <ListItemText primary={name} />
        </MenuItem>
      ))}
    </Select>
        </FormControl>
        <Button variant="contained" className="searchBtn" onClick={handleSearch}>
          Search
        </Button>
      </Box>
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Service Name</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Group</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Office ID</TableCell>
              <TableCell>Journey</TableCell>
              <TableCell>Cabin</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <Row key={index} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
    </>
  );
}
